/**
 * PrintCenterPage — Unified Print Center
 *
 * Aggregates document templates from all domains (TMS/WMS/FMS/Finance)
 * and provides a unified print queue with preview + rendering.
 *
 * API:
 *   GET  /api/v1/print-center/templates  → templates grouped by domain
 *   POST /api/v1/print-center/jobs       → create print job
 *   GET  /api/v1/print-center/jobs       → print job queue
 */

import { useState, useEffect, useCallback } from 'react';
import { useRenderMode } from '@/hooks/useRenderMode';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Printer, Search, Loader2, FileText, Eye, RefreshCw,
  Truck, Ship, Package, DollarSign, LayoutGrid, Clock,
  CheckCircle2, XCircle, AlertTriangle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DocTemplate {
  id: string;
  name: string;
  doc_type: string;
  description: string | null;
  is_default: boolean;
  page_size: string;
  orientation: string;
}

interface TemplateDomain {
  domain: string;
  label: string;
  templates: DocTemplate[];
}

interface PrintJob {
  id: string;
  template_id: string;
  entity_type: string;
  entity_id: string;
  copies: number;
  format: string;
  status: string;
  created_by: string;
  created_at: string;
}

const DOMAIN_ICONS: Record<string, typeof Truck> = {
  tms: Truck,
  fms: Ship,
  wms: Package,
  finance: DollarSign,
  other: FileText,
};

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  printed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-600',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function PrintCenterPage() {
  const isMobile = useRenderMode() === 'mobile';
  const [tab, setTab] = useState('templates');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState<TemplateDomain[]>([]);
  const [printJobs, setPrintJobs] = useState<PrintJob[]>([]);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [apiError, setApiError] = useState<DataErrorDetail | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get<{ data?: { domains?: TemplateDomain[] } }>('/print-center/templates');
      setDomains(res?.data?.domains ?? []);
    } catch {
      setDomains([]);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await api.get<{ items?: PrintJob[] }>('/print-center/jobs?limit=50');
      setPrintJobs(res?.items ?? []);
    } catch {
      setPrintJobs([]);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      await Promise.all([fetchTemplates(), fetchJobs()]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setApiError({ status: 500, code: 'FETCH_ERROR', message: msg, endpoint: '/print-center' });
    } finally {
      setLoading(false);
    }
  }, [fetchTemplates, fetchJobs]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePreview = async (templateId: string) => {
    try {
      const res = await api.post<{ html?: string }>(
        `/document-templates/${templateId}/render`,
        { data: {} },
      );
      setPreviewHtml(res?.html ?? '<p>No preview available</p>');
    } catch {
      setPreviewHtml('<p>Preview failed — template may require entity data.</p>');
    }
  };

  const totalTemplates = domains.reduce((s, d) => s + d.templates.length, 0);
  const q = search.toLowerCase();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // ── Preview Modal ──────────────────────────────────────────────────────────
  if (previewHtml) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Print Preview</h2>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setPreviewHtml(null)}>
              Back
            </Button>
            <Button size="sm" onClick={() => window.print()}>
              <Printer className="mr-1 h-3.5 w-3.5" /> Print
            </Button>
          </div>
        </div>
        <Card>
          <CardContent className="p-4">
            <div
              className="border rounded bg-white p-6 min-h-[400px] print:border-0 print:p-0"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {apiError && <DataErrorBanner error={apiError} entityLabel="Print Center" onRetry={fetchData} />}

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <TabsList className="h-8 text-xs">
            <TabsTrigger value="templates" className="flex items-center gap-1">
              <LayoutGrid className="h-3 w-3" /> Templates ({totalTemplates})
            </TabsTrigger>
            <TabsTrigger value="queue" className="flex items-center gap-1">
              <Clock className="h-3 w-3" /> Print Queue ({printJobs.length})
            </TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input placeholder="Search…" className="pl-8 h-8 w-44 text-xs"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Button size="sm" variant="outline" className="h-8" onClick={fetchData}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* ── Templates Tab ─────────────────────────────────────────────── */}
        <TabsContent value="templates">
          {domains.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground text-sm">
                <Printer className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>No document templates found.</p>
                <p className="text-xs mt-1">Create templates in Admin → Document Templates</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {domains.map((domain) => {
                const DomainIcon = DOMAIN_ICONS[domain.domain] ?? FileText;
                const filtered = domain.templates.filter(
                  (t) => !q || t.name.toLowerCase().includes(q) || t.doc_type.toLowerCase().includes(q),
                );
                if (filtered.length === 0 && q) return null;
                return (
                  <Card key={domain.domain}>
                    <CardHeader className="py-3 px-4">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <DomainIcon className="h-4 w-4" />
                        {domain.label}
                        <Badge variant="outline" className="text-[10px] ml-1">{domain.templates.length}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className={isMobile ? 'space-y-2 p-3' : 'grid grid-cols-2 lg:grid-cols-3 gap-3 p-4'}>
                        {filtered.map((tpl) => (
                          <Card key={tpl.id} className="hover:shadow-sm transition-shadow">
                            <CardContent className="p-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                    <span className="text-xs font-medium truncate">{tpl.name}</span>
                                    {tpl.is_default && (
                                      <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 shrink-0">默认</Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1.5 text-[10px] text-muted-foreground">
                                    <Badge variant="outline" className="text-[9px]">{tpl.doc_type}</Badge>
                                    <span>{tpl.page_size} · {tpl.orientation}</span>
                                  </div>
                                  {tpl.description && (
                                    <p className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{tpl.description}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-1.5 mt-2">
                                <Button size="sm" variant="outline" className="h-6 text-[10px] flex-1"
                                  onClick={() => handlePreview(tpl.id)}>
                                  <Eye className="mr-0.5 h-2.5 w-2.5" /> Preview
                                </Button>
                                <Button size="sm" className="h-6 text-[10px] flex-1"
                                  onClick={() => handlePreview(tpl.id)}>
                                  <Printer className="mr-0.5 h-2.5 w-2.5" /> Print
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Print Queue Tab ───────────────────────────────────────────── */}
        <TabsContent value="queue">
          {printJobs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground text-sm">
                <Clock className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>No print jobs in queue.</p>
                <p className="text-xs mt-1">Select a template and print a document to get started</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/30">
                    <tr>
                      {['Job ID', 'Entity', 'Format', 'Copies', 'Status', 'Created', ''].map((h) => (
                        <th key={h} className="text-left px-3 py-2.5 text-xs text-muted-foreground font-medium whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {printJobs.filter(j => !q || j.entity_type.toLowerCase().includes(q) || j.entity_id.toLowerCase().includes(q)).map((job) => (
                      <tr key={job.id} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="px-3 py-3 font-mono text-xs text-primary">{job.id.slice(0, 8)}</td>
                        <td className="px-3 py-3 text-xs">
                          <span className="font-mono">{job.entity_type}</span>
                          <span className="text-muted-foreground"> / {job.entity_id.slice(0, 8)}</span>
                        </td>
                        <td className="px-3 py-3">
                          <Badge variant="outline" className="text-[10px]">{job.format}</Badge>
                        </td>
                        <td className="px-3 py-3 text-xs text-center">{job.copies}</td>
                        <td className="px-3 py-3">
                          <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[job.status] ?? ''}`}>
                            {job.status === 'printed' && <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />}
                            {job.status === 'failed' && <XCircle className="h-2.5 w-2.5 mr-0.5" />}
                            {job.status === 'pending' && <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />}
                            {job.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-3 text-[10px] text-muted-foreground">
                          {new Date(job.created_at).toLocaleString('en-SG', { dateStyle: 'short', timeStyle: 'short' })}
                        </td>
                        <td className="px-3 py-3">
                          <Button size="sm" variant="ghost" className="h-6 text-[10px]">
                            <Eye className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
